import React, { useMemo, useState } from 'react'
import { Combobox, ComboboxInput, ComboboxList, ComboboxOption, ComboboxPopover } from '@reach/combobox'

interface OrganizationCandidate {
  id: number
  name: string
}

interface Props {
  candidates: OrganizationCandidate[]
  onChange: (organizationId: string) => void
  disabled?: boolean
}

const matchesSearch = (candidate: OrganizationCandidate, search: string): boolean =>
  candidate.name.toLowerCase().includes(search.trim().toLowerCase())

export function OrganizationPicker ({ candidates, onChange, disabled = false }: Props): React.ReactElement {
  const [search, setSearch] = useState('')

  const visibleCandidates = useMemo(
    () => candidates.filter((candidate) => matchesSearch(candidate, search)),
    [candidates, search]
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value)
    onChange('')
  }

  const handleSelect = (name: string): void => {
    setSearch(name)

    const selected = candidates.find((candidate) => candidate.name === name)
    onChange(selected === undefined ? '' : String(selected.id))
  }

  return (
    <div className='user-organization-picker'>
      <Combobox aria-labelledby='organization-label' onSelect={handleSelect} openOnFocus>
        <div className='user-organization-picker__field'>
          <label className='pro-text-m pro-bold' id='organization-label' htmlFor='organization-name'>Organization</label>
          <div className='user-organization-picker__control'>
            <ComboboxInput
              id='organization-name'
              className='pro-text-s'
              placeholder='Select'
              onChange={handleSearchChange}
              disabled={disabled}
            />
            <i className='fa-solid fa-sort user-organization-picker__icon' />
          </div>
        </div>

        <ComboboxPopover className='user-organization-picker__options'>
          {visibleCandidates.length > 0
            ? (
              <ComboboxList>
                {visibleCandidates.map((candidate) => (
                  <ComboboxOption key={candidate.id} className='pro-text-s' title={candidate.name} value={candidate.name} />
                ))}
              </ComboboxList>
              )
            : (
              <p className='pro-text-s user-organization-picker__no-matches' role='status'>
                No organizations match that search.
              </p>
              )}
        </ComboboxPopover>
      </Combobox>
    </div>
  )
}
